import React, { ReactNode } from 'react'
import styled from '@emotion/styled'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { Icon, type IconName } from '@mobi/component-library/Assets/Icon/Icon'
import { Flex } from '@mobi/component-library/Misc/Flex'

type Props = {
  shouldShowChevron: boolean
  children: ReactNode
  size: 'sm' | 'xs'
  isDisabled?: boolean
  iconName: IconName
  shouldStackChildren?: boolean
  onClick?(): void
}

export const SelectorPanelButton: React.FC<Props> = ({
  shouldShowChevron = false,
  children,
  size,
  isDisabled = false,
  iconName,
  shouldStackChildren = false,
  onClick = () => null,
}) => {
  return (
    <WrapperStyled size={size} data-is-disabled={isDisabled} data-size={size} onClick={onClick}>
      <div>
        <Icon
          name={iconName}
          color={isDisabled ? colors.neutral['900'] : colors.lavender['600']}
          size={sizeMap[size].iconSize}
        />
      </div>

      {shouldStackChildren ? <Flex flexDirection='column'>{children}</Flex> : children}

      {shouldShowChevron && (
        <Icon name='SolidChevronRight' color={colors.neutral['900']} size={spacing.big} />
      )}
    </WrapperStyled>
  )
}

type SizeMap = Record<
  Props['size'],
  {
    iconWrapperSize: string
    iconSize: string
    wrapperPadding: string
  }
>

const sizeMap: SizeMap = {
  sm: {
    iconWrapperSize: '3.2rem',
    iconSize: '1.6rem',
    wrapperPadding: spacing.md,
  },
  xs: {
    iconWrapperSize: '2.4rem',
    iconSize: '1.4rem',
    wrapperPadding: spacing.sm,
  },
}

const WrapperStyled = styled.div<Omit<Props, 'iconName' | 'shouldShowChevron'>>({
  display: 'grid',
  columnGap: spacing.sm,
  position: 'relative',
  gridTemplateColumns: 'auto 1fr auto auto',
  alignItems: 'center',
  fontFamily: font.family.primary,
  fontWeight: font.weight.medium,
  ...font.size.lg,
  color: colors.neutral['900'],
  borderRadius: radius.lgx1,
  backgroundColor: colors.white,
  border: `1px solid ${colors.neutral['200']}`,

  '&:focus-within': {
    outlineColor: colors.lavender['50'],
  },
  '&[data-is-disabled="true"]': {
    backgroundColor: colors.neutral['200'],
    border: `1px solid ${colors.neutral['300']}`,
  },
  '&[data-size="sm"]': {
    padding: sizeMap.sm.wrapperPadding,
  },
  '&[data-size="xs"]': {
    padding: sizeMap.xs.wrapperPadding,
  },

  '> div:first-child': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: radius.pill,
    backgroundColor: colors.lavender['100'],
  },
  '&[data-is-disabled="true"] > div:first-child': {
    backgroundColor: colors.neutral['200'],
  },
  '&[data-size="xs"] > div:first-child': {
    width: sizeMap.xs.iconWrapperSize,
    height: sizeMap.xs.iconWrapperSize,
  },
  '&[data-size="sm"] > div:first-child': {
    width: sizeMap.sm.iconWrapperSize,
    height: sizeMap.sm.iconWrapperSize,
  },
})
